import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import { ThemeProvider } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChartLine, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Select, message } from 'antd';
import moment from 'moment';
import {
  Container,
  HeaderSection,
  BackButton,
  TitleSection,
  Title,
  Subtitle,
  FiltersCard,
  FiltersTitle,
  FiltersGrid,
  FilterGroup,
  FilterLabel,
  SearchInputContainer,
  SearchInput,
  SectionHeader,
  SectionTitle,
  ItemsCount,
  ItemsGrid,
  PeriodCard,
  PeriodGrid,
  PeriodItem,
  PeriodLabel,
  PeriodValue,
  LoadingContainer,
} from './styles';
import PopularItemsSummary from './PopularItemsSummary';
import PopularItemCard from './PopularItemCard';
import PopularItemsTable from './PopularItemsTable';
import { fetchPopularItems } from './store/popularItemsSlice';
import { AppDispatch } from '../../../redux/store';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { useTheme } from '../../../context/ThemeContext';
import DateRangePicker from '../../../commonComponents/DateRangePicker';
import StoresDropDown from '../StoresDropDown';
import { DEFAULT_DATE_RANGE, DEFAULT_LIMIT, LIMIT_OPTIONS } from './constants';
import { DateRange } from './types';

const PopularItems: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { popularItemsData, loading, error } = useSelector(
    (state: any) => state.popularItems
  );

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [merchant, setMerchant] = useState<any>(null);
  const [storeId, setStoreId] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (user?.role === 'merchant' && !merchant) {
      setMerchant(user);
    }
  }, [user, merchant]);

  useEffect(() => {
    const fetchUserStores = async () => {
      if (user?.role !== 'admin' && !storeId) {
        try {
          const token = localStorage.getItem('access_token');
          const API = (await import('../../../context/API')).default;
          const res = await API.get('/stores', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const stores = res.data.data || [];
          if (stores.length > 0) {
            setStoreId(stores[0].id);
          }
        } catch (error) {
          console.error('Error fetching user stores:', error);
        }
      }
    };

    fetchUserStores();
  }, [user, storeId]);

  useEffect(() => {
    if (merchant?.id || user?.role === 'admin') {
      const params = {
        startDate: moment(selectedDateRange.startDate).format('YYYY-MM-DD'),
        endDate: moment(selectedDateRange.endDate).format('YYYY-MM-DD'),
        merchantId: merchant?.id,
        store: storeId,
        limit: limit,
        ...(selectedCategory && { categoryId: selectedCategory }),
      };

      dispatch(fetchPopularItems(params));
    }
  }, [dispatch, merchant?.id, selectedDateRange, storeId, selectedCategory, limit, user?.role]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);


  const filteredItems =
    popularItemsData?.items?.filter(
      (item) =>
        item.item.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const theme = {
    mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark',
  };

  const antdTheme = getAntdThemeConfig(isDarkMode);

  const isMobile = window.innerWidth < 768;

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={antdTheme}>
        <Container>
          <HeaderSection>
            <BackButton
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={() => navigate(-1)}
              aria-label="Go Back to Analytics Dashboard"
            />
            <TitleSection>
              <Title>
                <FontAwesomeIcon icon={faChartLine} />
                Popular Items Analysis
              </Title>
              <Subtitle>Track your most popular menu items and their performance</Subtitle>
            </TitleSection>
          </HeaderSection>

          <FiltersCard>
            <FiltersTitle>Filters & Settings</FiltersTitle>
            <FiltersGrid>
              <FilterGroup>
                <FilterLabel>Date Range</FilterLabel>
                <DateRangePicker
                  selectedDateRange={selectedDateRange}
                  onDateRangeChange={setSelectedDateRange}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Store</FilterLabel>
                <StoresDropDown
                  onOptionSelect={(store: any) => setStoreId(store?.id || '')}
                  merchantId={merchant?.id}
                  role={user?.role}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Category</FilterLabel>
                <Select
                  placeholder="All Categories"
                  value={selectedCategory || undefined}
                  onChange={setSelectedCategory}
                  style={{ width: '100%' }}
                  size="middle"
                >
                  <Select.Option value="">All Categories</Select.Option>
                </Select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Items to Show</FilterLabel>
                <Select
                  value={limit}
                  onChange={setLimit}
                  style={{ width: '100%' }}
                  size="middle"
                >
                  {LIMIT_OPTIONS.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </FilterGroup>
            </FiltersGrid>

            <SearchInputContainer>
              <FilterLabel>Search</FilterLabel>
              <SearchInput
                placeholder="Search items or categories"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                prefix={<FontAwesomeIcon icon={faSearch} />}
                size="middle"
                allowClear
              />
            </SearchInputContainer>
          </FiltersCard>

          {popularItemsData?.summary && (
            <PopularItemsSummary
              summary={popularItemsData.summary}
              borderColor="#4299E1"
            />
          )}

          {loading ? (
            <LoadingContainer>
              <div>Loading...</div>
            </LoadingContainer>
          ) : (
            <>
              <SectionHeader>
                <SectionTitle>Top {limit} Popular Items</SectionTitle>
                <ItemsCount>
                  Showing {filteredItems.length} of {popularItemsData?.totalItems || 0} items
                </ItemsCount>
              </SectionHeader>

              {isMobile ? (
                <ItemsGrid>
                  {filteredItems.map((item, index) => (
                    <PopularItemCard key={index} item={item} index={index} />
                  ))}
                </ItemsGrid>
              ) : (
                <PopularItemsTable items={filteredItems} isLoading={loading} />
              )}
            </>
          )}

          {popularItemsData?.period && (
            <PeriodCard>
              <SectionTitle>Analysis Period</SectionTitle>
              <PeriodGrid>
                <PeriodItem>
                  <PeriodLabel>Current Period</PeriodLabel>
                  <PeriodValue>
                    {moment(popularItemsData.period.current.startDate).format('MMM DD, YYYY')} -{' '}
                    {moment(popularItemsData.period.current.endDate).format('MMM DD, YYYY')}
                  </PeriodValue>
                </PeriodItem>
                <PeriodItem>
                  <PeriodLabel>Previous Period</PeriodLabel>
                  <PeriodValue>
                    {moment(popularItemsData.period.previous.startDate).format('MMM DD, YYYY')} -{' '}
                    {moment(popularItemsData.period.previous.endDate).format('MMM DD, YYYY')}
                  </PeriodValue>
                </PeriodItem>
              </PeriodGrid>
            </PeriodCard>
          )}
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default PopularItems;
